import SearchIcon from "@mui/icons-material/Search";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { Avatar } from "@src/appV2/lib/Avatar";
import { useOmnisearch } from "@src/appV2/Omnisearch";
import { Icon } from "@src/appV2/redesign/components/Icon";
import { Text } from "@src/appV2/redesign/components/Text";
import { useModalState } from "@src/lib/ui-react";
import { useCallback } from "react";

import { type AccountSwitcherUser } from "../../Shared/AccountSwitcher/types";
import { useAccountSwitcher } from "../../Shared/AccountSwitcher/useAccountSwitcher";
import { WorkplaceSelectorSheet } from "./WorkplaceSelectorSheet";

export function AccountSwitcherMobile() {
  const { admin, user } = useSession();

  if (admin) {
    return <AdminSwitcherMobile />;
  }

  // Workplace users only get the switcher if they actually have another
  // workplace to switch to — a single-workplace user has nothing to pick.
  const workplaceCount = user?.facilityIds?.length ?? 0;
  if (workplaceCount <= 1) {
    return null;
  }

  return <WorkplaceSwitcherMobile />;
}

/**
 * Admin: full-width "search field" styled trigger that opens the shared
 * Omnisearch dialog (same one Cmd+K opens on desktop).
 */
function AdminSwitcherMobile() {
  const { open: openOmnisearch } = useOmnisearch();

  return (
    <Tooltip title="Search agents or workplaces">
      <ButtonBase
        onClick={openOmnisearch}
        aria-label="Open search"
        sx={(theme) => ({
          width: "100%",
          minWidth: 0,
          height: 52,
          paddingX: 4,
          gap: 3,
          justifyContent: "flex-start",
          borderRadius: theme.borderRadius?.medium,
          backgroundColor: theme.palette.navbar?.itemSelectedBackground,
          color: theme.palette.navbar?.itemContent,
          transition: "background-color 120ms ease, color 120ms ease",
          "&:hover": {
            color: theme.palette.navbar?.itemContentSelected,
          },
        })}
      >
        <SearchIcon />
        <Text variant="body1" semibold>
          Search
        </Text>
      </ButtonBase>
    </Tooltip>
  );
}

/**
 * Workplace user: avatar button opens a bottom sheet listing the associated
 * workplaces the user can switch into.
 */
function WorkplaceSwitcherMobile() {
  const { switcherTitle, switcherSubtitle, workplaceDetails, onUserClicked } = useAccountSwitcher();
  const { profile } = useSession();
  const sheetModalState = useModalState();

  // Workplace name appears in the drawer title, so the switcher button focuses
  // on the user (subtitle = user name) — avoids redundantly repeating the
  // facility name on both rows.
  const buttonLabel = switcherSubtitle ?? switcherTitle;

  const handleWorkplaceSelected = useCallback(
    (selected: AccountSwitcherUser) => {
      sheetModalState.closeModal();
      onUserClicked(selected);
    },
    [onUserClicked, sheetModalState]
  );

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <ButtonBase
        onClick={sheetModalState.openModal}
        aria-label={`Switch account (current: ${buttonLabel})`}
        aria-haspopup="dialog"
        aria-expanded={sheetModalState.modalIsOpen}
        sx={(theme) => ({
          width: "100%",
          minWidth: 0,
          minHeight: 52,
          paddingX: 3,
          paddingY: 2,
          gap: 3,
          borderRadius: theme.borderRadius?.medium,
          backgroundColor: theme.palette.navbar?.itemSelectedBackground,
          color: theme.palette.navbar?.itemContent,
        })}
      >
        <Avatar
          name={buttonLabel}
          size="small"
          sx={(theme) => ({
            bgcolor: theme.palette.navbar?.avatarBackground,
            color: theme.palette.navbar?.avatarText,
            flexShrink: 0,
          })}
        />
        <Text
          variant="body1"
          semibold
          textAlign="left"
          noWrap
          sx={(theme) => ({ flex: 1, minWidth: 0, color: theme.palette.navbar?.userName })}
        >
          {buttonLabel}
        </Text>
        <Icon type="caret-bottom" sx={{ flexShrink: 0 }} />
      </ButtonBase>

      <WorkplaceSelectorSheet
        modalState={sheetModalState}
        workplaceDetails={workplaceDetails}
        selectedWorkplaceId={profile?.userId}
        onWorkplaceSelected={handleWorkplaceSelected}
      />
    </Box>
  );
}
